import { LinearProgress, MenuItem, Select } from '@mui/material';
import { useCallback, useState } from 'react';
import { formatEnumForDisplay } from '@/utils';
import { useUpdatePointOfInterestsStatusMutation, PoiStatus } from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { primary } from '@/theme/colors';

type Props = {
  pointOfInterestId: string;
  status: PoiStatus | null | undefined;
};

export const WorkFlowStatusChangeDropDown = ({ pointOfInterestId, status }: Props) => {
  const [selectedStatus, setSelectedStatus] = useState<PoiStatus | ''>(status || '');
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const [updatePoisStatus, { loading }] = useUpdatePointOfInterestsStatusMutation();

  const handleStatusChange = useCallback(
    (updatedStatus: PoiStatus) => {
      updatePoisStatus({
        variables: {
          input: {
            pointOfInterestIds: [pointOfInterestId],
            status: updatedStatus,
          },
        },
        onCompleted: () => {
          showSnackBar('POI status has been updated successfully', 'success');
          setSelectedStatus(updatedStatus);
        },
        onError: (error) => {
          showSnackBar(error.message, 'error');
        },
      });
    },
    [showSnackBar, updatePoisStatus, pointOfInterestId]
  );

  const renderValue = (value: PoiStatus | '') => {
    return value ? (
      formatEnumForDisplay(value)
    ) : (
      <span style={{ color: primary.LightGrey }}>Update Status</span>
    );
  };

  return (
    <>
      {loading ? (
        <LinearProgress color="info" sx={{ height: 7 }} />
      ) : (
        <Select
          value={selectedStatus}
          onChange={({ target: { value } }) => handleStatusChange(value as PoiStatus)}
          sx={{ height: 32, width: 'fit-content', fontSize: '1.2rem' }}
          fullWidth
          displayEmpty
          renderValue={renderValue}
        >
          {Object.values(PoiStatus).map((status) => (
            <MenuItem key={status} value={status}>
              {formatEnumForDisplay(status)}
            </MenuItem>
          ))}
        </Select>
      )}
    </>
  );
};
